import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2, Database, AlertCircle } from "lucide-react";
import { 
  initializeAdminListeners, 
  cleanupAdminListeners,
  selectSystemStatus,
  selectSystemStatusLoading,
  selectQueueStatus,
  selectQueueStatusLoading,
  selectAlgoliaUsage,
  selectAlgoliaUsageLoading,
  selectCollectionStats,
  selectCollectionStatsLoading,
  selectIsAlgoliaEnabled,
  selectIsAggregationEnabled
} from '@/store/slices/adminSlice';

interface AdminStatusDisplayProps {
  variant?: 'compact' | 'full';
}

export const AdminStatusDisplay: React.FC<AdminStatusDisplayProps> = ({ 
  variant = 'full' 
}) => {
  const dispatch = useAppDispatch();
  
  // Select data from adminSlice
  const systemStatus = useAppSelector(selectSystemStatus);
  const systemLoading = useAppSelector(selectSystemStatusLoading);
  const queueStatus = useAppSelector(selectQueueStatus);
  const queueLoading = useAppSelector(selectQueueStatusLoading);
  const algoliaUsage = useAppSelector(selectAlgoliaUsage);
  const algoliaLoading = useAppSelector(selectAlgoliaUsageLoading);
  const collectionStats = useAppSelector(selectCollectionStats);
  const collectionLoading = useAppSelector(selectCollectionStatsLoading);
  
  const isAlgoliaEnabled = useAppSelector(selectIsAlgoliaEnabled);
  const isAggregationEnabled = useAppSelector(selectIsAggregationEnabled);

  // Initialize listeners when component mounts
  useEffect(() => {
    dispatch(initializeAdminListeners());
    
    // Cleanup listeners when component unmounts
    return () => {
      dispatch(cleanupAdminListeners());
    };
  }, [dispatch]);

  // Format timestamp to human-readable date
  const formatTimestamp = (timestamp?: number): string => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  // Format queue age (e.g., "5 minutes ago")
  const formatQueueAge = (timestamp?: number): string => {
    if (!timestamp) return 'Unknown';
    
    const now = Date.now();
    const diff = now - timestamp;
    
    // Convert to seconds, minutes, hours, days
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds} ${seconds === 1 ? 'second' : 'seconds'} ago`;
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    
    const days = Math.floor(hours / 24);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  };

  // If we're still loading all data, show a loading state
  const isLoading = systemLoading || queueLoading || algoliaLoading || collectionLoading;
  
  if (isLoading && !systemStatus && !queueStatus && !algoliaUsage && !collectionStats) {
    return (
      <Card className={variant === 'compact' ? 'p-2' : ''}>
        <CardContent className="pt-4 flex items-center justify-center flex-col">
          <Loader2 className="h-6 w-6 animate-spin mb-2" />
          <p className="text-sm text-muted-foreground">Loading admin statistics...</p>
        </CardContent>
      </Card>
    );
  }

  // For compact variant, show a simplified version with just the key stats
  if (variant === 'compact') {
    return (
      <div className="space-y-2">
        {/* System Status Badges */}
        <div className="flex flex-wrap gap-1.5">
          <Badge variant={isAlgoliaEnabled ? 'default' : 'destructive'} className="text-xs">
            Algolia: {isAlgoliaEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
          <Badge variant={isAggregationEnabled ? 'default' : 'destructive'} className="text-xs">
            Aggregation: {isAggregationEnabled ? 'Enabled' : 'Disabled'}
          </Badge>
        </div>
        
        {/* Queue Status */}
        {queueStatus && (
          <div className="text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Queue:</span>
              <span className="font-medium">{queueStatus.queueSize} pending / {queueStatus.failedQueueSize} failed</span>
            </div>
            
            {queueStatus.queueSize > 0 && queueStatus.oldestQueuedItem && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Oldest item:</span>
                <span className="font-medium">{formatQueueAge(queueStatus.oldestQueuedItem)}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Algolia Usage */}
        {algoliaUsage && (
          <div className="text-xs space-y-1">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Records:</span>
              <span className="font-medium">
                {algoliaUsage.totalRecords.toLocaleString()} total
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Breakdown:</span>
              <span className="font-medium">
                {algoliaUsage.indices.drinks.objectCount} drinks / {algoliaUsage.indices.locations.objectCount} locations
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last sync:</span>
              <span className="font-medium">
                {algoliaUsage.updatedAt ? formatQueueAge(algoliaUsage.updatedAt) : 'Never'}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Full variant
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Admin Dashboard</span>
          <div className="flex gap-1">
            {systemStatus && (
              <>
                <Badge variant={isAlgoliaEnabled ? 'default' : 'destructive'}>
                  Algolia: {isAlgoliaEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
                <Badge variant={isAggregationEnabled ? 'default' : 'destructive'}>
                  Aggregation: {isAggregationEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="system" className="w-full">
          <TabsList className="grid grid-cols-4 mb-4">
            <TabsTrigger value="system">System</TabsTrigger>
            <TabsTrigger value="algolia">Algolia</TabsTrigger>
            <TabsTrigger value="queue">Queue</TabsTrigger>
            <TabsTrigger value="collections">Collections</TabsTrigger>
          </TabsList>
          
          {/* System Status Tab */}
          <TabsContent value="system" className="space-y-4">
            {systemStatus ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold">Algolia Sync</h3>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <span className={`font-medium ${systemStatus.algolia.status === 'enabled' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {systemStatus.algolia.status === 'enabled' ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Status Change:</span>
                        <span className="font-medium">{formatTimestamp(systemStatus.algolia.lastStatusChange)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Full Sync:</span>
                        <span className="font-medium">{formatTimestamp(systemStatus.algolia.lastFullSyncTime)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold">Aggregation</h3>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Status:</span>
                        <span className={`font-medium ${systemStatus.aggregation.status === 'enabled' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {systemStatus.aggregation.status === 'enabled' ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Status Change:</span>
                        <span className="font-medium">{formatTimestamp(systemStatus.aggregation.lastStatusChange)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Full Recalculation:</span>
                        <span className="font-medium">{formatTimestamp(systemStatus.aggregation.lastFullSyncTime)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center flex-col p-8">
                {systemLoading ? (
                  <>
                    <Loader2 className="h-8 w-8 animate-spin mb-4" />
                    <p>Loading system status...</p>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-8 w-8 text-amber-500 mb-4" />
                    <p>System status data not available</p>
                  </>
                )}
              </div>
            )}
          </TabsContent>
          
          {/* Algolia Usage Tab */}
          <TabsContent value="algolia" className="space-y-4">
            {algoliaUsage ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-semibold">Algolia Records</h3>
                  <Badge variant="outline">
                    Last updated: {formatQueueAge(algoliaUsage.updatedAt)}
                  </Badge>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Total Records</span>
                    <span className="font-semibold">{algoliaUsage.totalRecords.toLocaleString()}</span>
                  </div>
                  <Progress value={100} />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Drinks</span>
                      <span className="font-semibold">{algoliaUsage.indices.drinks.objectCount.toLocaleString()}</span>
                    </div>
                    <Progress 
                      value={algoliaUsage.totalRecords ? (algoliaUsage.indices.drinks.objectCount / algoliaUsage.totalRecords) * 100 : 0} 
                      className="h-2" 
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Locations</span>
                      <span className="font-semibold">{algoliaUsage.indices.locations.objectCount.toLocaleString()}</span>
                    </div>
                    <Progress 
                      value={algoliaUsage.totalRecords ? (algoliaUsage.indices.locations.objectCount / algoliaUsage.totalRecords) * 100 : 0} 
                      className="h-2" 
                    />
                  </div>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-sm font-semibold mb-2">Last 30 Days Operations</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-md p-3">
                      <div className="text-xs text-muted-foreground">Searches</div>
                      <div className="text-2xl font-bold">{algoliaUsage.operations.searches.toLocaleString()}</div>
                    </div>
                    <div className="border rounded-md p-3">
                      <div className="text-xs text-muted-foreground">Record Operations</div>
                      <div className="text-2xl font-bold">{algoliaUsage.operations.records.added.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center flex-col p-8">
                {algoliaLoading ? (
                  <>
                    <Loader2 className="h-8 w-8 animate-spin mb-4" />
                    <p>Loading Algolia usage statistics...</p>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-8 w-8 text-amber-500 mb-4" />
                    <p>Algolia usage data not available</p>
                  </>
                )}
              </div>
            )}
          </TabsContent>
          
          {/* Queue Status Tab */}
          <TabsContent value="queue" className="space-y-4">
            {queueStatus ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="border rounded-md p-4">
                    <div className="text-sm text-muted-foreground mb-1">Pending Updates</div>
                    <div className="text-3xl font-bold">{queueStatus.queueSize}</div>
                    {queueStatus.queueSize > 0 && queueStatus.oldestQueuedItem && (
                      <div className="text-xs text-muted-foreground mt-2">
                        Oldest: {formatQueueAge(queueStatus.oldestQueuedItem)}
                      </div>
                    )}
                    {queueStatus.queueSize > 0 && queueStatus.newestQueuedItem && (
                      <div className="text-xs text-muted-foreground">
                        Newest: {formatQueueAge(queueStatus.newestQueuedItem)}
                      </div>
                    )}
                  </div>
                  
                  <div className="border rounded-md p-4">
                    <div className="text-sm text-muted-foreground mb-1">Failed Updates</div>
                    <div className="text-3xl font-bold">{queueStatus.failedQueueSize}</div>
                    {queueStatus.failedQueueSize > 0 && (
                      <div className="text-xs text-amber-500 mt-2">
                        These updates require manual intervention
                      </div>
                    )}
                  </div>
                </div>
                
                {queueStatus.queueSize > 0 && (
                  <div className="border-t pt-4">
                    <div className="text-sm font-medium mb-2">Queue Status</div>
                    <div className="text-xs text-muted-foreground">
                      {queueStatus.queueSize > 100 ? (
                        <div className="text-amber-500 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          Large queue backlog detected. Consider running full sync operation.
                        </div>
                      ) : queueStatus.queueSize > 0 ? (
                        <div>Queue is being processed automatically.</div>
                      ) : (
                        <div>Queue is empty. All changes are synchronized.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center flex-col p-8">
                {queueLoading ? (
                  <>
                    <Loader2 className="h-8 w-8 animate-spin mb-4" />
                    <p>Loading queue status...</p>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-8 w-8 text-amber-500 mb-4" />
                    <p>Queue status data not available</p>
                  </>
                )}
              </div>
            )}
          </TabsContent>
          
          {/* Collections Tab */}
          <TabsContent value="collections" className="space-y-4">
            {collectionStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="border rounded-md p-3">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-sm font-medium">Locations</div>
                      <Database className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="text-2xl font-bold">{collectionStats.locations.toLocaleString()}</div>
                  </div>
                  
                  <div className="border rounded-md p-3">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-sm font-medium">Drinks</div>
                      <Database className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="text-2xl font-bold">{collectionStats.drinks.toLocaleString()}</div>
                  </div>
                  
                  <div className="border rounded-md p-3">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-sm font-medium">Brands</div>
                      <Database className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="text-2xl font-bold">{collectionStats.brands.toLocaleString()}</div>
                  </div>
                  
                  <div className="border rounded-md p-3">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-sm font-medium">Sightings</div>
                      <Database className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="text-2xl font-bold">{collectionStats.sightings.toLocaleString()}</div>
                  </div>
                  
                  <div className="border rounded-md p-3">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-sm font-medium">Reviews</div>
                      <Database className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="text-2xl font-bold">{collectionStats.reviews.toLocaleString()}</div>
                  </div>
                  
                  <div className="border rounded-md p-3">
                    <div className="flex justify-between items-center mb-1">
                      <div className="text-sm font-medium">Users</div>
                      <Database className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="text-2xl font-bold">{collectionStats.users.toLocaleString()}</div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold mb-2">Queue Collections</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex justify-between text-sm">
                      <span>Algolia Update Queue</span>
                      <span className="font-medium">{collectionStats.algoliaUpdateQueue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Algolia Failed Queue</span>
                      <span className="font-medium">{collectionStats.algoliaFailedQueue.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center flex-col p-8">
                {collectionLoading ? (
                  <>
                    <Loader2 className="h-8 w-8 animate-spin mb-4" />
                    <p>Loading collection statistics...</p>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-8 w-8 text-amber-500 mb-4" />
                    <p>Collection statistics not available</p>
                  </>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AdminStatusDisplay; 